import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { StatusBadge } from '../components/StatusBadge';
import { LoadsSpreadsheet, ListColumnManager, ALL_LIST_COLUMNS, RESTRICTED_LIST_COLUMNS, useListColumns } from '../components/loads/LoadsSpreadsheet';
import { FilterChipBar } from '../components/FilterChipBar';
import { BorderWatchlist } from '../components/BorderWatchlist';
import { isLoadStuck } from '../lib/crossBorder';
import { ACTIVE_LOAD_STATUSES } from '../components/load-workspace/StatusBar';
import { CheckCallsBoard } from '../components/check-calls/CheckCallsBoard';
import { TeamCustomerFilters, LOADS_FILTERS_KEY, readFilters, makeLoadFilter } from '../components/TeamCustomerFilters';
import { loadAPI, facilityAPI, userAPI, customerAPI, teamAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import { Plus, LayoutGrid, List, GripVertical, X, Settings, Filter, Search, MapPin, Users, Check } from 'lucide-react';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SchedulingIndicator, formatLocation, formatShortLocation, formatAppointmentDateTime, ApptTime, dropDays } from '../components/loads/kanbanUtils';
import { MobileKanbanBoard, useIsMobile } from '../components/loads/MobileKanban';
import { CbCardInfo, CbReviewQueueDialog } from '../components/loads/CrossBorderCard';
export { dropDays };

const ALL_STATUSES = [
  { id: 'Booked', title: 'Booked' },
  { id: 'Available', title: 'Available' },
  { id: 'Dispatched', title: 'Dispatched' },
  { id: 'Covered', title: 'Covered' },
  { id: 'Rate Con Sent', title: 'Rate Con Sent' },
  { id: 'Change', title: 'Change' },
  { id: 'Recovering', title: 'Recovering' },
  { id: 'At the Shipper', title: 'At the Shipper' },
  { id: 'In Transit', title: 'In Transit' },
  { id: 'In Transit MX', title: 'In Transit MX' },
  { id: 'At Border MX', title: 'At Border MX' },
  { id: 'At Border US', title: 'At Border US' },
  { id: 'In Transit US', title: 'In Transit US' },
  { id: 'At Delivery', title: 'At Delivery' },
  { id: 'Delivered', title: 'Delivered' },
  { id: 'Released', title: 'Released' },
];

// Restricted statuses for Customer and Carrier users
const CUSTOMER_CARRIER_STATUSES = [
  { id: 'Booked', title: 'Booked' },
  { id: 'Available', title: 'Available' },
  { id: 'Covered', title: 'Covered' },
  { id: 'At the Shipper', title: 'At the Shipper' },
  { id: 'In Transit', title: 'In Transit' },
  { id: 'Delivered', title: 'Delivered' },
];

const DEFAULT_COLUMNS = ['Booked', 'Dispatched', 'At the Shipper', 'In Transit', 'Delivered'];
const CUSTOMER_CARRIER_DEFAULT_COLUMNS = ['Booked', 'At the Shipper', 'In Transit', 'Delivered'];

const FILTER_OPTIONS = [
  { id: 'all', label: 'All Loads' },
  { id: 'this_week', label: 'Picking Up This Week' },
  { id: 'picking_today', label: 'Picking Up Today' },
  { id: 'delivering_today', label: 'Delivering Today' },
  { id: 'uncovered', label: 'Uncovered (No Carrier)' },
  { id: 'active', label: 'Active (At Shipper / In Transit)' },
  { id: 'unscheduled', label: 'Unschedule' },
  { id: 'not_released', label: 'Not Released' },
];

// Sunday-to-Saturday range (YYYY-MM-DD, end exclusive) for the current week
const currentWeekRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
  const end = new Date(start); end.setDate(start.getDate() + 7);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [iso(start), iso(end)];
};
const firstPickupDate = (load) => {
  const pickups = (load.stops || []).filter(s => s.type === 'pickup' && s.appointment_datetime).map(s => s.appointment_datetime).sort();
  const dt = pickups[0] || load.shipping_date;
  return dt ? String(dt).slice(0, 10) : null;
};

// Restricted filters for Customer and Carrier users
const CUSTOMER_CARRIER_FILTER_OPTIONS = [
  { id: 'all', label: 'All Loads' },
  { id: 'picking_today', label: 'Picking Up Today' },
  { id: 'delivering_today', label: 'Delivering Today' },
];

const ALL_CB_STATUSES = [
  { id: 'Available', title: 'Available' },
  { id: 'Covered', title: 'Covered' },
  { id: 'Rate Con Sent', title: 'Rate Con Sent' },
  { id: 'At the Shipper', title: 'At the Shipper' },
  { id: 'In Transit MX', title: 'In Transit MX' },
  { id: 'At Border MX', title: 'At Border MX' },
  { id: 'At Border US', title: 'At Border US' },
  { id: 'In Transit US', title: 'In Transit US' },
  { id: 'At Delivery', title: 'At Delivery' },
  { id: 'Delivered', title: 'Delivered' },
  { id: 'Released', title: 'Released' },
];

const DEFAULT_CB_COLUMNS = ['Available', 'Covered', 'Rate Con Sent', 'At the Shipper', 'In Transit MX', 'At Border MX', 'At Border US', 'In Transit US', 'Delivered'];

const CB_STATS_CARDS = [
  { id: 'card_drop_trailer', title: 'Drop Off Trailers' },
  { id: 'card_border_mx', title: 'At Border MX' },
  { id: 'card_border_us', title: 'At Border US' },
];

const DEFAULT_CB_CARDS = ['card_drop_trailer', 'card_border_mx', 'card_border_us'];

// Legacy array for backwards compatibility
const CB_STATUSES = DEFAULT_CB_COLUMNS;

// User Filter Dropdown Component
const UserFilterDropdown = ({ users, selectedUsers, onToggleUser, onClose, onClearAll, onSelectAll }) => {
  return (
    <>
      {/* Mobile overlay */}
      <div 
        className="fixed inset-0 bg-black/30 z-40 md:hidden" 
        onClick={onClose}
      />
      <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-1/4 md:top-12 bg-[var(--tms-card)] border rounded-lg shadow-lg p-4 z-50 md:w-72">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[var(--tms-text)]">Filter by User</h4>
          <button onClick={onClose} className="p-1 hover:bg-[var(--tms-hover)] rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mb-3">
          <button 
            onClick={onSelectAll}
            className="text-xs text-[var(--tms-primary)] hover:underline"
          >
            Select All
          </button>
          <span className="text-[var(--tms-text-muted)]">|</span>
          <button 
            onClick={onClearAll}
            className="text-xs text-[var(--tms-text-secondary)] hover:underline"
          >
            Clear All
          </button>
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {users.map(user => (
            <label 
              key={user.id} 
              className={`flex items-center gap-2 cursor-pointer hover:bg-[var(--tms-hover)] p-2 rounded ${
                selectedUsers.includes(user.id) ? 'bg-[var(--tms-primary)]/10' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={selectedUsers.includes(user.id)}
                onChange={() => onToggleUser(user.id)}
                className="rounded border-[var(--tms-border)]"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-[var(--tms-text-secondary)] block truncate">{user.name}</span>
                <span className="text-xs text-[var(--tms-text-muted)] capitalize">{user.role?.replace('_', ' ')}</span>
              </div>
            </label>
          ))}
        </div>
        <p className="text-xs text-[var(--tms-text-muted)] mt-3 pt-3 border-t">
          {selectedUsers.length === 0 ? 'Showing all loads' : `Showing loads for ${selectedUsers.length} user(s)`}
        </p>
      </div>
    </>
  );
};

// Scheduling status color indicator
const LoadCard = ({ load, facilities, onDelete, onToggleTracking, showCb = false }) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: load.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const pickupStops = load.stops?.filter(s => s.type === 'pickup') || [];
  const deliveryStops = load.stops?.filter(s => s.type === 'delivery') || [];
  const origin = pickupStops[0];
  const destinations = deliveryStops;

  const handleCardClick = (e) => {
    // Only navigate if not dragging
    if (!isDragging) {
      navigate(`/loads/${load.id}`);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      if (window.confirm('Are you sure you want to delete this load?')) {
        onDelete(load.id);
      }
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleCardClick}
      className={`kanban-card cursor-pointer hover:shadow-md transition-shadow relative group ${showCb && (load.cb?.overdue || load.cb?.has_errors) ? 'ring-2 ring-red-300' : showCb && load.cb?.needs_review ? 'ring-2 ring-amber-300' : ''}`}
      data-testid={`load-card-${load.id}`}
    >
      {onDelete && (
        <button 
          onClick={handleDelete}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-red-100 hover:bg-red-200 rounded text-[var(--tms-error)] transition-opacity z-10"
          title="Delete load"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      {/* Row 1: Load # */}
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-sm font-bold text-[var(--tms-text)]">
          {load.load_number || load.id.substring(0, 8).toUpperCase()}
        </span>
        {load.cross_border && (
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded mr-6">XB</span>
        )}
      </div>
      
      {/* Row 2: Customer, Carrier */}
      <div className="mb-2 pb-2 border-b border-[var(--tms-border)]">
        <p className="text-sm font-medium text-[var(--tms-text)] truncate">{load.customer_name}</p>
        {load.carrier_name && (
          <p className="text-xs text-[var(--tms-text-muted)] truncate">🚚 {load.carrier_name}</p>
        )}
      </div>
      
      {/* Row 3: Origin with scheduling indicator in front */}
      <div className="mb-2">
        <p className="text-xs text-[var(--tms-text-muted)] font-medium mb-1">ORIGIN</p>
        <div className="flex items-center gap-2">
          <SchedulingIndicator status={origin?.appointment_status} />
          <span className="flex-1 truncate text-sm text-[var(--tms-text-secondary)]" title={formatLocation(origin, facilities)}>
            {formatShortLocation(origin, facilities)}
          </span>
          <ApptTime value={origin?.appointment_datetime} endTime={origin?.appointment_end_time} testId={`card-pu-appt-${load.id}`} />
        </div>
      </div>
      
      {/* Row 4: Destination/Stops with scheduling indicators in front */}
      <div className="mb-2">
        <p className="text-xs text-[var(--tms-text-muted)] font-medium mb-1">
          DESTINATION{destinations.length > 1 ? ` (${destinations.length} stops)` : ''}
        </p>
        <div className="space-y-1">
          {destinations.length > 0 ? (
            destinations.map((stop, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <SchedulingIndicator status={stop.appointment_status} />
                <span
                  className={`truncate flex-1 ${idx === 0 ? 'text-sm text-[var(--tms-text-secondary)]' : 'text-xs text-[var(--tms-text-muted)]'}`}
                  title={formatLocation(stop, facilities)}
                >
                  {formatShortLocation(stop, facilities)}
                </span>
                <ApptTime value={stop.appointment_datetime} endTime={stop.appointment_end_time} testId={`card-del-appt-${load.id}-${idx}`} />
              </div>
            ))
          ) : (
            <div className="flex items-center gap-2">
              <SchedulingIndicator status={null} />
              <span className="text-sm text-[var(--tms-text-muted)]">N/A</span>
            </div>
          )}
        </div>
      </div>
      
      {showCb && <CbCardInfo cb={load.cb} loadId={load.id} />}

      {/* Tracking toggle (Tracking tab only) */}
      {onToggleTracking && (
        <div className="pt-2 border-t border-[var(--tms-border)] flex items-center justify-end">
          <label
            className="flex items-center gap-1.5 text-xs font-semibold text-[var(--tms-text-secondary)] cursor-pointer select-none"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={!!load.tracking}
              onChange={(e) => onToggleTracking(load.id, e.target.checked)}
              className="w-4 h-4 accent-[var(--tms-primary)]"
              data-testid={`tracking-checkbox-${load.id}`}
            />
            Tracking
          </label>
        </div>
      )}
    </div>
  );
};

const StopCard = ({ item, facilities }) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const { load, stop, stopIndex } = item;
  const isPickup = stop.type === 'pickup';
  const ref = isPickup ? (stop.references?.[0] || '') : (stop.po_number || '');
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      {...attributes}
      {...listeners}
      onClick={() => { if (!isDragging) navigate(`/loads/${load.id}`); }}
      className="kanban-card cursor-pointer hover:shadow-md transition-shadow"
      data-testid={`stop-card-${load.id}-${stopIndex}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-sm font-bold text-[var(--tms-text)]">{load.load_number || load.id.substring(0, 8).toUpperCase()}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isPickup ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {isPickup ? 'PICKUP' : 'DELIVERY'} {stop.stop_number || stopIndex + 1}
        </span>
      </div>
      <p className="text-xs text-[var(--tms-text-muted)] truncate mb-2 pb-2 border-b border-[var(--tms-border)]">{load.customer_name}</p>
      <div className="flex items-center gap-2">
        <SchedulingIndicator status={stop.appointment_status} />
        <span className="flex-1 truncate text-sm text-[var(--tms-text-secondary)]" title={formatLocation(stop, facilities)}>
          {stop.facility_name || formatShortLocation(stop, facilities)}
        </span>
        <ApptTime value={stop.appointment_datetime} endTime={stop.appointment_end_time} testId={`stop-card-appt-${load.id}-${stopIndex}`} />
      </div>
      <div className="flex items-center justify-between mt-1 text-xs text-[var(--tms-text-muted)]">
        <span className="truncate">{formatShortLocation(stop, facilities)}</span>
        {ref && <span className="font-mono ml-2 shrink-0">{isPickup ? 'PU' : 'PO'} {ref}</span>}
      </div>
    </div>
  );
};

const SortableColumn = ({ column, loads, facilities, onRemove, onDeleteLoad, showDropDays = false, showCbDays = false, onToggleTracking, renderCard, unitLabel = 'loads' }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="kanban-column" data-testid={`column-${column.id}`}>
      <div className="p-3 border-b border-[var(--tms-border)] flex items-center justify-between">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <GripVertical className="w-4 h-4 text-[var(--tms-text-muted)] cursor-grab" />
          <div>
            <h3 className="font-semibold text-[var(--tms-text)]">{column.title}</h3>
            <span className="text-xs text-[var(--tms-text-muted)]">{loads.length} {unitLabel}</span>
          </div>
        </div>
        <button 
          onClick={() => onRemove(column.id)}
          className="p-1 hover:bg-red-100 rounded text-[var(--tms-text-muted)] hover:text-[var(--tms-error)]"
          title="Remove column"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3 space-y-2 min-h-[400px]">
        <SortableContext items={loads.map(l => l.id)} strategy={verticalListSortingStrategy}>
          {loads.map((load) => (
            <div key={load.id} className="relative">
              {showDropDays && (() => {
                const ds = (load.stops || []).filter(s => s.drop_off && s.trailer_dropped_date);
                const days = ds.length ? dropDays(ds[ds.length - 1]) : null;
                return days !== null ? (
                  <span
                    className="absolute -top-1 -right-1 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white shadow"
                    data-testid={`drop-days-${load.id}`}
                  >
                    {days}d
                  </span>
                ) : null;
              })()}
              {showCbDays && (() => {
                const start = load.cb_status_changed_at ? new Date(load.cb_status_changed_at) : new Date();
                const days = Math.max(0, Math.round((Date.now() - start.getTime()) / 86400000));
                return (
                  <span
                    className="absolute -top-1 -right-1 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white shadow"
                    data-testid={`cb-days-${load.id}`}
                  >
                    {days}d
                  </span>
                );
              })()}
              {renderCard ? renderCard(load) : <LoadCard load={load} facilities={facilities} onDelete={onDeleteLoad} onToggleTracking={onToggleTracking} />}
            </div>
          ))}
        </SortableContext>
      </div>
    </div>
  );
};

const ColumnManager = ({ activeColumns, onToggleColumn, onClose, carrierColumns, xbColumns, dropColumns, schedColumns, trackingColumns, onToggleModeColumn }) => {
  const GROUPS = [
    { key: 'load', title: 'Load Status', items: ['Booked', 'Dispatched', 'At the Shipper', 'In Transit', 'Delivered', 'Released'], active: activeColumns, toggle: onToggleColumn },
    { key: 'carrier', title: 'Carrier Assignment', items: ['Available', 'Setting Up', 'Covered', 'RC Sent', 'Verified', 'Change', 'Recovering'], active: carrierColumns, toggle: (id) => onToggleModeColumn('carrier', id) },
    { key: 'xb', title: 'Cross-Border Stage', items: ['In Transit MX', 'At Border MX', 'At Border US', 'In Transit US', 'At Border CA', 'In Transit CA'], active: xbColumns, toggle: (id) => onToggleModeColumn('xb', id) },
    { key: 'drop', title: 'Drop Off Trailers', items: ['At the Shipper', 'Waiting for Review', 'At the Delivery'], active: dropColumns, toggle: (id) => onToggleModeColumn('drop', id) },
    { key: 'sched', title: 'Scheduling', items: ['Needs Scheduling', 'Requested', 'Updated TMS', 'Change', 'Granted'], active: schedColumns, toggle: (id) => onToggleModeColumn('sched', id) },
    { key: 'tracking', title: 'Tracking', items: ['Not Tracking', 'Tracking'], active: trackingColumns, toggle: (id) => onToggleModeColumn('tracking', id) },
  ];
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={onClose} />
      <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-1/4 md:top-12 bg-[var(--tms-card)] border rounded-lg shadow-lg p-4 z-50 md:w-64">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[var(--tms-text)]">Manage Columns</h4>
          <button onClick={onClose} className="p-1 hover:bg-[var(--tms-hover)] rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {GROUPS.map((g, gi) => (
            <div key={g.key} className={gi > 0 ? 'pt-2 mt-2 border-t border-[var(--tms-border)]' : ''}>
              <p className="text-sm font-bold text-[var(--tms-text)] mb-1" data-testid={`column-group-${g.key}`}>{g.title}</p>
              <div className="space-y-0.5">
                {g.items.map(id => (
                  <label key={id} className="flex items-center gap-2 cursor-pointer hover:bg-[var(--tms-hover)] py-1 px-2 ml-3 rounded">
                    <input
                      type="checkbox"
                      checked={g.active.includes(id)}
                      onChange={() => g.toggle(id)}
                      className="rounded border-[var(--tms-border)]"
                      data-testid={`column-toggle-${g.key}-${id}`}
                    />
                    <span className="text-sm text-[var(--tms-text)]">{id}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const CBColumnManager = ({ activeColumns, onToggleColumn, activeCards, onToggleCard, onClose }) => {
  return (
    <>
      {/* Mobile overlay */}
      <div 
        className="fixed inset-0 bg-black/30 z-40 md:hidden" 
        onClick={onClose}
      />
      <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-1/4 md:top-12 bg-[var(--tms-card)] border rounded-lg shadow-lg p-4 z-50 md:w-64">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[var(--tms-text)]">Manage CB View</h4>
          <button onClick={onClose} className="p-1 hover:bg-[var(--tms-hover)] rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <p className="text-xs font-medium text-[var(--tms-text-secondary)] mb-2">Stats Cards</p>
        <div className="space-y-1 mb-3">
          {CB_STATS_CARDS.map(card => (
            <label key={card.id} className="flex items-center gap-2 cursor-pointer hover:bg-[var(--tms-hover)] p-1.5 rounded">
              <input
                type="checkbox"
                checked={activeCards.includes(card.id)}
                onChange={() => onToggleCard(card.id)}
                className="rounded border-[var(--tms-border)]"
              />
              <span className="text-sm text-[var(--tms-text-secondary)]">{card.title}</span>
            </label>
          ))}
        </div>
        
        <div className="border-t border-[var(--tms-border)] pt-2 mb-2">
          <p className="text-xs font-medium text-[var(--tms-text-secondary)] mb-2">Kanban Columns</p>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {ALL_CB_STATUSES.map(status => (
            <label key={status.id} className="flex items-center gap-2 cursor-pointer hover:bg-[var(--tms-hover)] p-1.5 rounded">
              <input
                type="checkbox"
                checked={activeColumns.includes(status.id)}
                onChange={() => onToggleColumn(status.id)}
                className="rounded border-[var(--tms-border)]"
              />
              <span className="text-sm text-[var(--tms-text-secondary)]">{status.title}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  );
};

const FilterDropdown = ({ activeFilter, onFilterChange, onClose, availableFilters, customers = [], selectedCustomerId, onCustomerChange }) => {
  const filters = availableFilters || FILTER_OPTIONS;
  const [customerSearch, setCustomerSearch] = React.useState('');
  const sortedCustomers = React.useMemo(() => {
    const list = (customers || []).filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase()));
    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [customers, customerSearch]);
  
  return (
    <>
      {/* Mobile overlay */}
      <div 
        className="fixed inset-0 bg-black/30 z-40 md:hidden" 
        onClick={onClose}
      />
      {/* Dropdown - fixed on mobile, absolute on desktop */}
      <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-1/4 md:top-12 bg-[var(--tms-card)] border rounded-lg shadow-lg p-4 z-50 md:w-72">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[var(--tms-text)]">Filter Loads</h4>
          <button onClick={onClose} className="p-1 hover:bg-[var(--tms-hover)] rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {filters.map(option => (
            <button
              key={option.id}
              onClick={() => { onFilterChange(option.id); onClose(); }}
              className={`w-full text-left px-3 py-2.5 md:py-2 rounded text-sm ${activeFilter === option.id ? 'bg-blue-100 text-blue-700' : 'hover:bg-[var(--tms-hover)] text-[var(--tms-text-secondary)]'}`}
              data-testid={`filter-option-${option.id}`}
            >
              {option.label}
            </button>
          ))}

          {customers.length > 0 && (
            <>
              <div className="border-t border-[var(--tms-border)] my-2" />
              <div className="px-1 mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-[var(--tms-text-muted)] uppercase tracking-wide">By Customer</span>
                {selectedCustomerId && (
                  <button
                    onClick={() => { onCustomerChange(null); onClose(); }}
                    className="text-xs text-[var(--tms-primary)] hover:underline"
                    data-testid="clear-customer-filter"
                  >
                    Clear
                  </button>
                )}
              </div>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search customers..."
                className="w-full mb-2 px-2 py-1.5 text-sm border rounded bg-[var(--tms-card)] text-[var(--tms-text)]"
                data-testid="customer-filter-search"
              />
              <button
                onClick={() => { onCustomerChange(null); onClose(); }}
                className={`w-full text-left px-3 py-2 rounded text-sm ${!selectedCustomerId ? 'bg-blue-100 text-blue-700' : 'hover:bg-[var(--tms-hover)] text-[var(--tms-text-secondary)]'}`}
                data-testid="customer-option-all"
              >
                All Customers
              </button>
              {sortedCustomers.map(c => (
                <button
                  key={c.id}
                  onClick={() => { onCustomerChange(c.id); onClose(); }}
                  className={`w-full text-left px-3 py-2 rounded text-sm ${selectedCustomerId === c.id ? 'bg-blue-100 text-blue-700' : 'hover:bg-[var(--tms-hover)] text-[var(--tms-text-secondary)]'}`}
                  data-testid={`customer-option-${c.id}`}
                >
                  {c.name}
                </button>
              ))}
              {sortedCustomers.length === 0 && (
                <p className="text-xs text-[var(--tms-text-muted)] px-3 py-2">No customers match</p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// Cross Border Load Card - Shows load with crossing documents
// Cross Border Load Card with delete button
const CrossBorderLoadCard = ({ load, onClick, onDelete, isDragging }) => {
  const crossingDocs = load.crossing_docs || {};
  const hasAnyDoc = Object.values(crossingDocs).some(v => v === true);
  
  // Get checked document names
  const checkedDocs = [];
  if (crossingDocs.ccp_layout) checkedDocs.push('CCP Layout');
  if (crossingDocs.cfdi) checkedDocs.push('CFDI');
  if (crossingDocs.w9) checkedDocs.push('W9');
  if (crossingDocs.doda) checkedDocs.push('DODA');
  if (crossingDocs.us_entry) checkedDocs.push('US Entry');
  if (crossingDocs.redo_documents) checkedDocs.push('ReDo Documents');

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(load.id);
  };

  return (
    <div 
      className={`bg-[var(--tms-card)] border rounded-lg p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow relative group ${isDragging ? 'opacity-50' : ''}`}
      onClick={onClick}
    >
      {onDelete && (
        <button 
          onClick={handleDelete}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-red-100 hover:bg-red-200 rounded text-[var(--tms-error)] transition-opacity"
          title="Delete load"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      <div className="font-semibold text-[var(--tms-text)] text-sm mb-1 pr-6">
        Load #{load.load_number || load.id.substring(0, 8)}
      </div>
      <div className="text-xs text-[var(--tms-text-muted)] mb-2">{load.customer_name}</div>
      
      {/* Crossing Dates */}
      <div className="text-xs space-y-1 mb-2">
        {load.crossing_docs_received_date && (
          <div className="flex items-center gap-1">
            <span className="text-[var(--tms-text-muted)]">Docs Rcvd:</span>
            <span className="font-medium text-[var(--tms-success)]">{formatDate(load.crossing_docs_received_date)}</span>
          </div>
        )}
        {load.crossing_docs_due_date && (
          <div className="flex items-center gap-1">
            <span className="text-[var(--tms-text-muted)]">Docs Due:</span>
            <span className={`font-medium ${new Date(load.crossing_docs_due_date) < new Date() ? 'text-[var(--tms-error)]' : 'text-amber-600'}`}>
              {formatDate(load.crossing_docs_due_date)}
            </span>
          </div>
        )}
        {load.crossing_eta_date && (
          <div className="flex items-center gap-1">
            <span className="text-[var(--tms-text-muted)]">ETA:</span>
            <span className="font-medium text-[var(--tms-primary)]">{formatDate(load.crossing_eta_date)}</span>
          </div>
        )}
      </div>

      {hasAnyDoc ? (
        <div className="flex flex-wrap gap-1">
          {checkedDocs.map(doc => (
            <span 
              key={doc} 
              className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded"
            >
              {doc}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-[var(--tms-text-muted)] italic">No documents checked</div>
      )}
    </div>
  );
};

// Draggable Cross Border Load Card
const DraggableCBLoadCard = ({ load, onClick, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: load.id,
    data: { load, type: 'cb-load' }
  });

  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  // Whole card is draggable so users can grab anywhere.
  // PointerSensor's distance:8 activation prevents click-vs-drag confusion —
  // a quick click still navigates, only a >8px move starts a drag.
  // The visible grip icon (top-right) is just a visual hint.
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group cursor-grab active:cursor-grabbing"
      data-testid={`cb-draggable-${load.id}`}
    >
      <div
        className="absolute right-1 top-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        title="Drag to move"
      >
        <GripVertical className="w-3.5 h-3.5 text-[var(--tms-text-muted)]" />
      </div>
      <CrossBorderLoadCard load={load} onClick={onClick} onDelete={onDelete} isDragging={isDragging} />
    </div>
  );
};

// Droppable wrapper for CB Kanban columns
const DroppableCBColumn = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`space-y-2 min-h-[200px] rounded transition-colors ${isOver ? 'bg-[var(--tms-primary)]/10 ring-2 ring-[var(--tms-primary)]/30' : ''}`}>
      {children}
    </div>
  );
};

// Sortable wrapper for an entire CB Kanban column (drag whole column to reorder)
const SortableCBColumn = ({ status, count, onRemove, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: status });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kanban-column flex-shrink-0 p-4"
      data-status={status}
      data-testid={`cb-column-${status}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="flex items-center gap-2 cursor-grab active:cursor-grabbing select-none flex-1"
          {...attributes}
          {...listeners}
          data-testid={`cb-column-handle-${status}`}
          title="Drag to reorder column"
        >
          <GripVertical className="w-4 h-4 text-[var(--tms-text-muted)]" />
          <h3 className="font-semibold text-[var(--tms-text-secondary)]">{status}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-[var(--tms-hover)] text-[var(--tms-text-secondary)] px-2 py-1 rounded-full">
            {count}
          </span>
          <button
            onClick={() => onRemove(status)}
            className="p-1 hover:bg-red-100 rounded text-[var(--tms-text-muted)] hover:text-[var(--tms-error)]"
            title="Remove column"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <DroppableCBColumn id={status}>
        {children}
      </DroppableCBColumn>
    </div>
  );
};

// Cross Border Kanban View - Groups loads by CB Status with drag-and-drop
const CrossBorderKanbanView = ({ loads, allLoads, navigate, onUpdateLoad, onDeleteLoad, activeCBColumns, onRemoveCBColumn, onReorderCBColumns, activeCBCards = DEFAULT_CB_CARDS }) => {
  const [docsDueDate, setDocsDueDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Filter loads that have docs due on selected date
  const docsDueLoads = loads.filter(load => {
    if (!load.crossing_docs_due_date) return false;
    const loadDueDate = load.crossing_docs_due_date.split('T')[0];
    return loadDueDate === docsDueDate;
  });

  // Group loads by status (unified) - only for active columns
  const groupedLoads = {};
  activeCBColumns.forEach(status => {
    groupedLoads[status] = loads.filter(load => (load.status || 'Available') === status);
  });

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || !active) return;

    const activeId = active.id;
    const overId = over.id;

    // 1) Column reordering: both ids are CB column ids
    const isColumnDrag = activeCBColumns.includes(activeId);
    if (isColumnDrag && activeCBColumns.includes(overId)) {
      if (activeId !== overId) {
        const oldIndex = activeCBColumns.indexOf(activeId);
        const newIndex = activeCBColumns.indexOf(overId);
        if (oldIndex !== -1 && newIndex !== -1 && onReorderCBColumns) {
          onReorderCBColumns(arrayMove(activeCBColumns, oldIndex, newIndex));
        }
      }
      return;
    }
    // If a column is being dragged but dropped on a non-column target, ignore (don't treat as load move)
    if (isColumnDrag) return;

    // 2) Load card moving between columns
    let newCBStatus = null;
    if (activeCBColumns.includes(overId)) {
      newCBStatus = overId;
    } else {
      const overLoad = loads.find(l => l.id === overId);
      if (overLoad) {
        newCBStatus = overLoad.status || 'Available';
      }
    }

    if (newCBStatus) {
      const load = loads.find(l => l.id === activeId);
      if (load && (load.status || 'Available') !== newCBStatus) {
        try {
          await onUpdateLoad(activeId, { status: newCBStatus });
          toast.success(`Load moved to ${newCBStatus}`);
        } catch (error) {
          toastApiError(toast, error, 'Load status could not be updated');
        }
      }
    }
  };

  const handleDelete = async (loadId) => {
    if (window.confirm('Are you sure you want to delete this load?')) {
      await onDeleteLoad(loadId);
    }
  };

  // Calculate days between a date and today
  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    const diff = Math.floor((today - date) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : 0;
  };

  return (
    <div>
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="cb-kanban-board">
        {/* Docs Due Column - First (not droppable) */}
        <div className="kanban-column flex-shrink-0 p-4 bg-[var(--tms-warning)]/10 border-[var(--tms-warning)]/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[var(--tms-warning)]">Docs Due</h3>
            <span className="text-xs bg-[var(--tms-warning)]/20 text-[var(--tms-warning)] px-2 py-1 rounded-full">
              {docsDueLoads.length}
            </span>
          </div>
          <div className="mb-3">
            <input
              type="date"
              value={docsDueDate}
              onChange={(e) => setDocsDueDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--tms-warning)]/50 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--tms-warning)] bg-[var(--tms-card)] text-[var(--tms-text)]"
              data-testid="docs-due-date-picker"
            />
          </div>
          <div className="space-y-2 min-h-[200px]">
            {docsDueLoads.length === 0 ? (
              <p className="text-xs text-[var(--tms-warning)] text-center py-4">No docs due on this date</p>
            ) : (
              docsDueLoads.map(load => (
                <DraggableCBLoadCard 
                  key={load.id} 
                  load={load} 
                  onClick={() => navigate(`/loads/${load.id}`)}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </div>

        {/* CB Status Columns - Droppable AND Sortable (drag column header to reorder) */}
        <SortableContext items={activeCBColumns} strategy={horizontalListSortingStrategy}>
          {activeCBColumns.map(status => (
            <SortableCBColumn
              key={status}
              status={status}
              count={groupedLoads[status]?.length || 0}
              onRemove={onRemoveCBColumn}
            >
              {groupedLoads[status]?.length === 0 ? (
                <p className="text-xs text-[var(--tms-text-muted)] text-center py-4">No loads</p>
              ) : (
                groupedLoads[status].map(load => (
                  <DraggableCBLoadCard
                    key={load.id}
                    load={load}
                    onClick={() => navigate(`/loads/${load.id}`)}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </SortableCBColumn>
          ))}
        </SortableContext>
      </div>
    </DndContext>
    </div>
  );
};

const applyCardOrder = (list) => {
  try {
    const saved = JSON.parse(localStorage.getItem('tms_kanban_card_order') || '[]');
    if (!saved.length) return list;
    const rank = new Map(saved.map((id, i) => [id, i]));
    return [...list].sort((a, b) => (rank.has(a.id) ? rank.get(a.id) : Infinity) - (rank.has(b.id) ? rank.get(b.id) : Infinity));
  } catch { return list; }
};
const persistCardOrder = (list) => {
  localStorage.setItem('tms_kanban_card_order', JSON.stringify(list.map(l => l.id)));
};

const Loads = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Persist view mode in localStorage
    const saved = localStorage.getItem('tms_loads_view_mode');
    return saved || 'kanban';
  });
  const [activeId, setActiveId] = useState(null);
  const [kanbanGroupBy, setKanbanGroupBy] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['load', 'carrier', 'xb', 'drop', 'sched', 'tracking', 'checkcalls'].includes(tabParam)) return tabParam;
    return localStorage.getItem('tms_kanban_groupby') || 'load';
  });
  const changeKanbanGroupBy = (mode) => {
    setKanbanGroupBy(mode);
    localStorage.setItem('tms_kanban_groupby', mode);
  };
  const [carrierColumns, setCarrierColumns] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('tms_carrier_columns') || 'null');
    return saved ? saved.map(c => c === 'Verify' ? 'Verified' : c) : ['Available', 'Setting Up', 'Covered', 'RC Sent', 'Verified', 'Change', 'Recovering'];
  });
  const [xbColumns, setXbColumns] = useState(() => (JSON.parse(localStorage.getItem('tms_xb_columns') || 'null') || ['In Transit MX', 'At Border MX', 'At Border US', 'In Transit US', 'At Border CA', 'In Transit CA']).map(c => c.replace('At the Border', 'At Border')));
  const [xbHistory, setXbHistory] = useState(false);
  const [xbReviewOpen, setXbReviewOpen] = useState(false);
  const [dropColumnsOrder, setDropColumnsOrder] = useState(() => JSON.parse(localStorage.getItem('tms_drop_columns_order') || 'null') || ['At the Shipper', 'Waiting for Review', 'At the Delivery']);
  const [schedColumnsOrder, setSchedColumnsOrder] = useState(() => JSON.parse(localStorage.getItem('tms_sched_columns_order') || 'null') || ['Needs Scheduling', 'Requested', 'Updated TMS', 'Change', 'Granted']);
  const [trackingColumnsOrder, setTrackingColumnsOrder] = useState(() => JSON.parse(localStorage.getItem('tms_tracking_columns') || 'null') || ['Not Tracking', 'Tracking']);
  const MODE_COLUMN_STATE = {
    carrier: [carrierColumns, setCarrierColumns, 'tms_carrier_columns'],
    xb: [xbColumns, setXbColumns, 'tms_xb_columns'],
    drop: [dropColumnsOrder, setDropColumnsOrder, 'tms_drop_columns_order'],
    sched: [schedColumnsOrder, setSchedColumnsOrder, 'tms_sched_columns_order'],
    tracking: [trackingColumnsOrder, setTrackingColumnsOrder, 'tms_tracking_columns'],
  };
  const handleToggleModeColumn = (mode, id) => {
    const entry = MODE_COLUMN_STATE[mode];
    if (!entry) return;
    const [, setCols, storeKey] = entry;
    setCols(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem(storeKey, JSON.stringify(next));
      return next;
    });
  };
  // Temporary filters only live in the URL (Dashboard KPI cards / customer profile); never persisted
  const activeFilter = searchParams.get('filter') || 'all';
  const tempCustomerId = searchParams.get('customer') || '';
  const hasTempFilters = activeFilter !== 'all' || !!tempCustomerId;
  const setActiveFilter = (id) => {
    const next = new URLSearchParams(searchParams);
    if (!id || id === 'all') next.delete('filter'); else next.set('filter', id);
    setSearchParams(next);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    const saved = localStorage.getItem('tms_loads_customer_filter');
    return saved || null;
  });
  const [cbOnly, setCbOnly] = useState(() => localStorage.getItem('tms_loads_cb_only') === '1');
  const [stuckOnly, setStuckOnly] = useState(() => localStorage.getItem('tms_loads_stuck_only') === '1');
  const [activeColumns, setActiveColumns] = useState(() => {
    const saved = localStorage.getItem('tms_kanban_columns');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });
  const [activeCBColumns, setActiveCBColumns] = useState(() => {
    const saved = localStorage.getItem('tms_cb_kanban_columns');
    return saved ? JSON.parse(saved).map(c => c.replace('At the Border', 'At Border')) : DEFAULT_CB_COLUMNS;
  });
  const [activeCBCards, setActiveCBCards] = useState(() => {
    const saved = localStorage.getItem('tms_cb_stats_cards');
    return saved ? JSON.parse(saved) : DEFAULT_CB_CARDS;
  });
  
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showListColumnManager, setShowListColumnManager] = useState(false);
  const [showCBColumnManager, setShowCBColumnManager] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserFilter, setShowUserFilter] = useState(false);
  // Operators / Customers filters belong to this page only and persist until the user changes or clears them
  const [teamFilters, setTeamFilters] = useState(() => readFilters(localStorage, LOADS_FILTERS_KEY));
  const [directory, setDirectory] = useState(null);
  const updateTeamFilters = (next) => {
    setTeamFilters(next);
    localStorage.setItem(LOADS_FILTERS_KEY, JSON.stringify(next));
  };
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const { isAdmin, isCarrier, isCustomer } = useAuth();
  const listColumns = useListColumns(isCarrier() || isCustomer());
  const { activeListColumns, setActiveListColumns } = listColumns;

  // Determine if user should see restricted options
  const isRestrictedUser = isCarrier() || isCustomer();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Save view mode to localStorage
  useEffect(() => {
    localStorage.setItem('tms_loads_view_mode', viewMode);
  }, [viewMode]);
  
  // Save selected customer filter to localStorage
  useEffect(() => {
    if (selectedCustomerId) {
      localStorage.setItem('tms_loads_customer_filter', selectedCustomerId);
    } else {
      localStorage.removeItem('tms_loads_customer_filter');
    }
  }, [selectedCustomerId]);

  // Persist quick-toggles
  useEffect(() => {
    if (cbOnly) localStorage.setItem('tms_loads_cb_only', '1');
    else localStorage.removeItem('tms_loads_cb_only');
  }, [cbOnly]);

  useEffect(() => {
    if (stuckOnly) localStorage.setItem('tms_loads_stuck_only', '1');
    else localStorage.removeItem('tms_loads_stuck_only');
  }, [stuckOnly]);
  
  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const res = await userAPI.getPreferences();
        const prefs = res.data?.preferences || {};
        
        // For restricted users, always use restricted columns regardless of saved preferences
        if (isRestrictedUser) {
          setActiveColumns(CUSTOMER_CARRIER_DEFAULT_COLUMNS);
        } else if (prefs.kanban_columns) {
          setActiveColumns(prefs.kanban_columns);
        }
        
        if (prefs.cb_kanban_columns) {
          setActiveCBColumns(prefs.cb_kanban_columns);
        }
        if (prefs.list_columns && !isRestrictedUser) {
          setActiveListColumns(prefs.list_columns);
        } else if (isRestrictedUser) {
          setActiveListColumns(RESTRICTED_LIST_COLUMNS);
        }
        
        setPreferencesLoaded(true);
      } catch (error) {
        console.error('Failed to load preferences:', error);
        // On error, still apply restricted columns for restricted users
        if (isRestrictedUser) {
          setActiveColumns(CUSTOMER_CARRIER_DEFAULT_COLUMNS);
        }
        setPreferencesLoaded(true);
      }
    };
    loadPreferences();
  }, [isRestrictedUser, setActiveListColumns]);


  // Save kanban columns to user preferences
  useEffect(() => {
    if (preferencesLoaded) {
      localStorage.setItem('tms_kanban_columns', JSON.stringify(activeColumns));
      // Also save to user preferences (debounced)
      const saveTimeout = setTimeout(() => {
        userAPI.updatePreferenceKey('kanban_columns', activeColumns).catch(() => {});
      }, 1000);
      return () => clearTimeout(saveTimeout);
    }
  }, [activeColumns, preferencesLoaded]);

  // Save CB kanban columns to user preferences
  useEffect(() => {
    if (preferencesLoaded) {
      localStorage.setItem('tms_cb_kanban_columns', JSON.stringify(activeCBColumns));
      // Also save to user preferences (debounced)
      const saveTimeout = setTimeout(() => {
        userAPI.updatePreferenceKey('cb_kanban_columns', activeCBColumns).catch(() => {});
      }, 1000);
      return () => clearTimeout(saveTimeout);
    }
  }, [activeCBColumns, preferencesLoaded]);

  // Save CB stats cards to localStorage
  useEffect(() => {
    if (preferencesLoaded) {
      localStorage.setItem('tms_cb_stats_cards', JSON.stringify(activeCBCards));
    }
  }, [activeCBCards, preferencesLoaded]);

  // Save list columns to user preferences
  useEffect(() => {
    if (preferencesLoaded && !isRestrictedUser) {
      localStorage.setItem('tms_list_columns', JSON.stringify(activeListColumns));
      const saveTimeout = setTimeout(() => {
        userAPI.updatePreferenceKey('list_columns', activeListColumns).catch(() => {});
      }, 1000);
      return () => clearTimeout(saveTimeout);
    }
  }, [activeListColumns, preferencesLoaded, isRestrictedUser]);

  // Save selected users filter to user preferences
  useEffect(() => {
    if (preferencesLoaded && selectedUsers.length > 0) {
      const saveTimeout = setTimeout(() => {
        userAPI.updatePreferenceKey('selected_users', selectedUsers).catch(() => {});
      }, 1000);
      return () => clearTimeout(saveTimeout);
    }
  }, [selectedUsers, preferencesLoaded]);

  // Manual kanban card order (persisted locally, applied across all tabs)

  const fetchLoads = useCallback(async () => {
    try {
      const loadsRes = await loadAPI.getAll();
      setLoads(applyCardOrder(loadsRes.data));
    } catch (error) {
      toast.error('Failed to load loads');
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [loadsRes, facilitiesRes, usersRes, customersRes] = await Promise.all([
        loadAPI.getAll(),
        facilityAPI.getAll(),
        userAPI.getDropdown(),
        customerAPI.getAll()
      ]);
      if (!isRestrictedUser) teamAPI.directory().then(r => setDirectory(r.data)).catch(() => {});
      setLoads(applyCardOrder(loadsRes.data));
      setFacilities(facilitiesRes.data);
      setUsers(usersRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [isRestrictedUser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Remember where the user was (page scroll + kanban horizontal scroll) so "Loads" brings them back to the same spot
  useEffect(() => {
    let raf = 0;
    const save = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const board = document.querySelector('[data-testid="kanban-board"], [data-testid="cb-kanban-board"], [data-testid="mobile-kanban-board"]');
        const list = document.querySelector('[data-testid="loads-table-scroll"]');
        sessionStorage.setItem('tms_loads_scroll', JSON.stringify({ y: window.scrollY, x: board ? board.scrollLeft : 0, listY: list ? list.scrollTop : 0 }));
      });
    };
    window.addEventListener('scroll', save, { passive: true });
    document.addEventListener('scroll', save, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', save);
      document.removeEventListener('scroll', save, { capture: true });
      cancelAnimationFrame(raf);
    };
  }, []);

  // Only when coming back from a load detail do we restore the previous scroll position
  const cameFromLoadRef = useRef((sessionStorage.getItem('tms_current_path') || '').startsWith('/loads/'));
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    if (!cameFromLoadRef.current) { sessionStorage.removeItem('tms_loads_scroll'); return; }
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem('tms_loads_scroll') || 'null'); } catch { saved = null; }
    if (!saved) return;
    let tries = 0;
    const restore = () => {
      const board = document.querySelector('[data-testid="kanban-board"], [data-testid="cb-kanban-board"], [data-testid="mobile-kanban-board"]');
      if (board && saved.x) board.scrollLeft = saved.x;
      const list = document.querySelector('[data-testid="loads-table-scroll"]');
      if (list && saved.listY) list.scrollTop = saved.listY;
      window.scrollTo(0, saved.y || 0);
      // Content can still be growing (images/rows); retry briefly until the target offset is reachable
      const listOff = list ? Math.abs(list.scrollTop - (saved.listY || 0)) : 0;
      if ((Math.abs(window.scrollY - (saved.y || 0)) > 2 || listOff > 2) && tries++ < 10) setTimeout(restore, 100);
    };
    requestAnimationFrame(restore);
  }, [loading]);

  // Standard list view starts at today's loads (or the next upcoming day) unless we're returning from a load
  useEffect(() => {
    if (loading || viewMode !== 'list' || cameFromLoadRef.current) return;
    const timer = setTimeout(() => {
      const container = document.querySelector('[data-testid="loads-table-scroll"]');
      if (!container) return;
      const todayKey = new Date().toLocaleDateString('en-CA');
      const headers = [...container.querySelectorAll('[data-day-key]')].filter(h => h.dataset.dayKey !== 'none');
      const target = headers.find(h => h.dataset.dayKey >= todayKey) || headers[headers.length - 1];
      if (!target) return;
      const theadH = container.querySelector('thead')?.getBoundingClientRect().height || 0;
      container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top - theadH;
    }, 150);
    return () => clearTimeout(timer);
  }, [loading, viewMode, activeFilter, tempCustomerId, teamFilters]);

  // Briefly highlight the load the user just came back from
  useEffect(() => {
    if (loading) return;
    const lastId = sessionStorage.getItem('tms_last_load_id');
    if (!lastId) return;
    sessionStorage.removeItem('tms_last_load_id');
    const timer = setTimeout(() => {
      const els = document.querySelectorAll(`[data-testid="load-card-${lastId}"], [data-testid="load-row-${lastId}"], [data-testid^="stop-card-${lastId}-"]`);
      if (!els.length) return;
      const first = els[0].getBoundingClientRect();
      if (first.bottom < 0 || first.top > window.innerHeight) els[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      els.forEach(el => el.classList.add('load-flash'));
      setTimeout(() => els.forEach(el => el.classList.remove('load-flash')), 3500);
    }, 450);
    return () => clearTimeout(timer);
  }, [loading]);

  // Refetch loads when the user filter changes (after the initial users list is loaded)
  const usersLoaded = users.length > 0;
  useEffect(() => {
    if (usersLoaded) fetchLoads();
  }, [usersLoaded, fetchLoads]);

  // User filter handlers
  const handleToggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    setSelectedUsers(users.map(u => u.id));
  };

  const handleClearAllUsers = () => {
    setSelectedUsers([]);
  };

  // Filter loads based on activeFilter, search query, and selected users
  const getFilteredLoads = () => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    
    const isDateToday = (dateStr) => {
      if (!dateStr) return false;
      // Handle both YYYY-MM-DD and datetime formats
      const dateOnly = dateStr.split('T')[0];
      return dateOnly === todayStr;
    };
    
    let filtered = loads;
    
    // Hide "Released" loads by default unless user is specifically searching for them
    const isSearchingForReleased = searchQuery.toLowerCase().includes('released');
    if (!isSearchingForReleased) {
      filtered = filtered.filter(load => load.status !== 'Released');
    }
    
    // Team member (customer-membership based) AND customer filters — same logic as the Dashboard
    filtered = filtered.filter(makeLoadFilter(teamFilters, directory));

    // Temporary customer filter (arriving from a customer profile)
    if (tempCustomerId) filtered = filtered.filter(load => load.customer_id === tempCustomerId);

    // Apply CB-only quick toggle
    if (cbOnly) {
      filtered = filtered.filter(load => load.cross_border === true);
    }

    // Apply Stuck quick toggle (loads at non-terminal status > 24h)
    if (stuckOnly) {
      filtered = filtered.filter(load => isLoadStuck(load));
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(load => {
        // Search by load number
        const loadNumber = (load.load_number || load.id.substring(0, 8)).toLowerCase();
        if (loadNumber.includes(query)) return true;
        
        // Search by customer name
        if (load.customer_name?.toLowerCase().includes(query)) return true;
        
        // Search by carrier name
        if (load.carrier_name?.toLowerCase().includes(query)) return true;
        
        // Search by commodity
        if (load.commodity?.toLowerCase().includes(query)) return true;
        
        // Search by equipment
        if (load.equipment?.toLowerCase().includes(query)) return true;
        
        // Search by status
        if (load.status?.toLowerCase().includes(query)) return true;
        
        // Search by driver name and phone
        if (load.driver_name?.toLowerCase().includes(query)) return true;
        if (load.driver_phone?.toLowerCase().includes(query)) return true;
        
        // Search by dispatch name and phone
        if (load.dispatch_name?.toLowerCase().includes(query)) return true;
        if (load.dispatch_phone?.toLowerCase().includes(query)) return true;
        
        // Search by truck/trailer numbers, LP, VIN
        if (load.truck_number?.toLowerCase().includes(query)) return true;
        if (load.truck_lp?.toLowerCase().includes(query)) return true;
        if (load.truck_vin?.toLowerCase().includes(query)) return true;
        if (load.trailer_number?.toLowerCase().includes(query)) return true;
        if (load.trailer_lp?.toLowerCase().includes(query)) return true;
        
        // Search by weight
        if (load.weight?.toString().includes(query)) return true;
        
        // Search by account manager or operator names
        if (load.account_manager_name?.toLowerCase().includes(query)) return true;
        if (load.operator_name?.toLowerCase().includes(query)) return true;
        
        // Search by origin/destination (facility names, cities, and PO numbers)
        const allStops = load.stops || [];
        const matchesStop = allStops.some(stop => {
          // Search by PO number
          if (stop.po_number?.toLowerCase().includes(query)) return true;
          
          // Search by facility name stored in stop
          if (stop.facility_name?.toLowerCase().includes(query)) return true;
          
          // Search by city/state stored in stop
          if (stop.city?.toLowerCase().includes(query)) return true;
          if (stop.state?.toLowerCase().includes(query)) return true;
          if (stop.zip_code?.toLowerCase().includes(query)) return true;
          
          // Search by facility details from facilities array
          const facility = facilities.find(f => f.id === stop.facility_id);
          if (facility) {
            if (facility.name?.toLowerCase().includes(query)) return true;
            if (facility.city?.toLowerCase().includes(query)) return true;
            if (facility.state?.toLowerCase().includes(query)) return true;
            if (facility.zip_code?.toLowerCase().includes(query)) return true;
            if (facility.address?.toLowerCase().includes(query)) return true;
          }
          return false;
        });
        if (matchesStop) return true;
        
        return false;
      });
    }
    
    // Apply date/status filter
    switch (activeFilter) {
      case 'picking_today':
        return filtered.filter(load => {
          // Check if shipping_date is today
          if (isDateToday(load.shipping_date)) return true;
          // Check pickup stop appointment dates
          const pickups = load.stops?.filter(s => s.type === 'pickup') || [];
          return pickups.some(s => isDateToday(s.appointment_datetime));
        });
      case 'delivering_today':
        return filtered.filter(load => {
          // Check delivery stop appointment dates
          const deliveries = load.stops?.filter(s => s.type === 'delivery') || [];
          return deliveries.some(s => isDateToday(s.appointment_datetime));
        });
      case 'unscheduled':
        return filtered.filter(load => !['Delivered', 'Released'].includes(load.status)
          && (load.stops || []).some(s => (s.appointment_status || 'Needs Scheduling') === 'Needs Scheduling'));
      case 'uncovered':
        return filtered.filter(load => !load.carrier_id && !['Delivered', 'Released'].includes(load.status));
      case 'active':
        return filtered.filter(load => ['At the Shipper', 'In Transit'].includes(load.status));
      case 'this_week': {
        const [ws, we] = currentWeekRange();
        return filtered.filter(load => { const d = firstPickupDate(load); return d && d >= ws && d < we; });
      }
      case 'not_released':
        // Show only "Delivered" status loads (delivered but not yet released)
        return filtered.filter(load => load.status === 'Delivered');
      default:
        return filtered;
    }
  };

  const filteredLoads = getFilteredLoads();

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Same-column drop: reorder cards manually (persists across all tabs)
    const dragLoad = loads.find(l => l.id === active.id);
    const hoverLoad = loads.find(l => l.id === over.id);
    if (dragLoad && hoverLoad && dragLoad.id !== hoverLoad.id) {
      const sameGroup =
        kanbanGroupBy === 'carrier' ? (dragLoad.carrier_status || 'Available') === (hoverLoad.carrier_status || 'Available')
        : kanbanGroupBy === 'xb' ? dragLoad.cb_status === hoverLoad.cb_status
        : kanbanGroupBy === 'drop' ? dropCategory(dragLoad) === dropCategory(hoverLoad)
        : kanbanGroupBy === 'sched' ? schedCategory(dragLoad) === schedCategory(hoverLoad)
        : kanbanGroupBy === 'tracking' ? trackingCategory(dragLoad) === trackingCategory(hoverLoad)
        : dragLoad.status === hoverLoad.status;
      if (sameGroup) {
        const oldIndex = loads.findIndex(l => l.id === dragLoad.id);
        const newIndex = loads.findIndex(l => l.id === hoverLoad.id);
        const next = arrayMove(loads, oldIndex, newIndex);
        setLoads(next);
        persistCardOrder(next);
        return;
      }
    }

    // Alternate group-by modes: update the mode's field instead of load status
    if (kanbanGroupBy !== 'load') {
      // Column drag: reorder this mode's columns and persist
      const entry = MODE_COLUMN_STATE[kanbanGroupBy];
      if (!entry) return;
      const [colOrder, setColOrder, storeKey] = entry;
      if (colOrder.includes(active.id)) {
        if (colOrder.includes(over.id) && active.id !== over.id) {
          const next = arrayMove(colOrder, colOrder.indexOf(active.id), colOrder.indexOf(over.id));
          setColOrder(next);
          localStorage.setItem(storeKey, JSON.stringify(next));
        }
        return;
      }
      // Tracking tab: dropping a card on the other column toggles the tracking flag
      if (kanbanGroupBy === 'tracking') {
        const activeLoad = loads.find(l => l.id === active.id);
        if (!activeLoad) return;
        let target = null;
        if (TRACKING_KANBAN_COLUMNS.includes(over.id)) target = over.id;
        else { const overLoad = loads.find(l => l.id === over.id); if (overLoad) target = trackingCategory(overLoad); }
        if (!target || trackingCategory(activeLoad) === target) return;
        await handleToggleTracking(activeLoad.id, target === 'Tracking');
        return;
      }
      // Scheduling tab: cards are individual stops — dropping on a column sets that stop's appointment status
      if (kanbanGroupBy === 'sched') {
        const [loadId, idxStr] = String(active.id).split('::');
        const stopIdx = parseInt(idxStr, 10);
        const activeLoad = loads.find(l => l.id === loadId);
        if (!activeLoad || isNaN(stopIdx)) return;
        let newStatus = null;
        if (colOrder.includes(over.id)) newStatus = over.id;
        else {
          const [overLoadId, overIdx] = String(over.id).split('::');
          const overLoad = loads.find(l => l.id === overLoadId);
          const overStop = overLoad?.stops?.[parseInt(overIdx, 10)];
          if (overStop) newStatus = overStop.appointment_status || 'Needs Scheduling';
        }
        const current = activeLoad.stops?.[stopIdx]?.appointment_status || 'Needs Scheduling';
        if (!newStatus || current === newStatus) return;
        const stops = (activeLoad.stops || []).map((s, i) => ({ ...s, facility_id: s.facility_id || '', facility_name: s.facility_name || '', ...(i === stopIdx ? { appointment_status: newStatus } : {}) }));
        try {
          const res = await loadAPI.update(activeLoad.id, { stops });
          const saved = res.data || {};
          setLoads(prev => prev.map(l => l.id === activeLoad.id ? { ...l, ...saved, stops: saved.stops || stops } : l));
          toast.success(`Stop moved to ${newStatus}`);
        } catch (err) {
          toastApiError(toast, err, 'Stop status could not be updated');
        }
        return;
      }
      if (kanbanGroupBy === 'drop') return; // derived card categories — cards not movable across columns
      if (kanbanGroupBy === 'xb') { toast.info('Cross-Border Stage is set from recorded border milestones — open the load and use Record Border Arrival / Departure'); return; }
      const field = kanbanGroupBy === 'carrier' ? 'carrier_status' : 'cb_status';
      const activeLoad = loads.find(l => l.id === active.id);
      if (!activeLoad) return;
      let newVal = null;
      if (colOrder.includes(over.id)) newVal = over.id;
      else {
        const overLoad = loads.find(l => l.id === over.id);
        if (overLoad) newVal = overLoad[field];
      }
      if (!newVal || activeLoad[field] === newVal) return;
      try {
        const res = await loadAPI.update(activeLoad.id, { [field]: newVal });
        const saved = res.data || {};
        setLoads(prev => prev.map(l => l.id === activeLoad.id ? { ...l, ...saved, stops: saved.stops || l.stops } : l));
        toast.success(`Load moved to ${newVal}`);
      } catch (err) {
        toastApiError(toast, err, 'Load could not be moved');
      }
      return;
    }

    // Check if we're reordering columns
    const isColumnDrag = activeColumns.includes(active.id);
    if (isColumnDrag && activeColumns.includes(over.id)) {
      const oldIndex = activeColumns.indexOf(active.id);
      const newIndex = activeColumns.indexOf(over.id);
      if (oldIndex !== newIndex) {
        setActiveColumns(arrayMove(activeColumns, oldIndex, newIndex));
      }
      return;
    }

    // Otherwise, handle load drag
    const activeLoad = loads.find(l => l.id === active.id);
    if (!activeLoad) return;

    // Find target column
    let newStatus = null;
    if (activeColumns.includes(over.id)) {
      newStatus = over.id;
    } else {
      const overLoad = loads.find(l => l.id === over.id);
      if (overLoad) {
        newStatus = overLoad.status;
      }
    }

    if (!newStatus || activeLoad.status === newStatus) return;
    const releaseStep = activeLoad.status === 'Delivered' && newStatus === 'Released';
    if (!isAdmin() && !releaseStep) {
      toast.info('Load Status is set automatically from carrier assignment and arrival/departure times. Only Delivered → Released can be moved by hand.');
      return;
    }

    try {
      const res = await loadAPI.update(activeLoad.id, { status: newStatus });
      setLoads(loads.map(l => l.id === activeLoad.id ? { ...l, ...(res.data || { status: newStatus }) } : l));
      toast.success(`Load moved to ${newStatus}`);
    } catch (error) {
      toastApiError(toast, error, 'Load status could not be updated');
    }
  };

  const handleUpdateLoadCB = async (loadId, updates) => {
    try {
      await loadAPI.update(loadId, updates);
      setLoads(loads.map(l => l.id === loadId ? { ...l, ...updates } : l));
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteLoad = async (loadId) => {
    const reason = window.prompt('Why are you deleting this load? (required)');
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A reason is required to delete'); return; }
    try {
      await loadAPI.delete(loadId, reason.trim());
      setLoads(loads.filter(l => l.id !== loadId));
      toast.success('Load deleted successfully');
    } catch (error) {
      toast.error('Failed to delete load');
    }
  };

  const handleToggleColumn = (columnId) => {
    if (activeColumns.includes(columnId)) {
      if (activeColumns.length > 1) {
        setActiveColumns(activeColumns.filter(c => c !== columnId));
      }
    } else {
      setActiveColumns([...activeColumns, columnId]);
    }
  };

  const handleRemoveColumn = (columnId) => {
    if (activeColumns.length > 1) {
      setActiveColumns(activeColumns.filter(c => c !== columnId));
    } else {
      toast.error('Must have at least one column');
    }
  };

  const handleToggleCBColumn = (columnId) => {
    if (activeCBColumns.includes(columnId)) {
      if (activeCBColumns.length > 1) {
        setActiveCBColumns(activeCBColumns.filter(c => c !== columnId));
      }
    } else {
      setActiveCBColumns([...activeCBColumns, columnId]);
    }
  };

  const handleRemoveCBColumn = (columnId) => {
    if (activeCBColumns.length > 1) {
      setActiveCBColumns(activeCBColumns.filter(c => c !== columnId));
    } else {
      toast.error('Must have at least one column');
    }
  };

  const handleToggleCBCard = (cardId) => {
    if (activeCBCards.includes(cardId)) {
      setActiveCBCards(activeCBCards.filter(c => c !== cardId));
    } else {
      setActiveCBCards([...activeCBCards, cardId]);
    }
  };

  // List column handlers
  const handleToggleListColumn = listColumns.toggleColumn;

  // Tracking checkbox: optimistic update so the card moves between Kanban columns instantly
  const handleToggleTracking = async (loadId, tracking) => {
    const prevLoads = loads;
    setLoads(prev => prev.map(l => l.id === loadId ? { ...l, tracking } : l));
    try {
      await loadAPI.update(loadId, { tracking });
      toast.success(tracking ? 'Tracking enabled' : 'Tracking disabled');
    } catch (err) {
      setLoads(prevLoads);
      toastApiError(toast, err, 'Tracking could not be updated');
    }
  };

  // Use appropriate status list based on user role
  const availableStatusList = isRestrictedUser ? CUSTOMER_CARRIER_STATUSES : ALL_STATUSES;
  const availableFilterList = isRestrictedUser ? CUSTOMER_CARRIER_FILTER_OPTIONS : FILTER_OPTIONS;
  
  // Columns are user-controlled: unchecking in Manage Columns or clicking X removes them.
  const columns = activeColumns.map(id => availableStatusList.find(s => s.id === id)).filter(Boolean);
  const groupedLoads = columns.reduce((acc, col) => {
    acc[col.id] = filteredLoads.filter(l => l.status === col.id);
    return acc;
  }, {});

  // ===== Kanban group-by modes =====
  const CARRIER_KANBAN_COLUMNS = ['Available', 'Setting Up', 'Covered', 'RC Sent', 'Verified', 'Change', 'Recovering'];
  const XB_KANBAN_COLUMNS = ['In Transit MX', 'At Border MX', 'At Border US', 'In Transit US', 'At Border CA', 'In Transit CA'];
  const DROP_KANBAN_COLUMNS = ['At the Shipper', 'Waiting for Review', 'At the Delivery'];
  const SCHED_KANBAN_COLUMNS = ['Needs Scheduling', 'Requested', 'Updated TMS', 'Change', 'Granted'];
  const TRACKING_KANBAN_COLUMNS = ['Not Tracking', 'Tracking'];
  const trackingCategory = (l) => (l.tracking ? 'Tracking' : 'Not Tracking');
  const schedCategory = (load) => {
    const statuses = (load.stops || []).map(s => s.appointment_status || 'Needs Scheduling');
    if (statuses.length === 0) return 'Needs Scheduling';
    for (const st of ['Needs Scheduling', 'Change', 'Requested', 'Updated TMS']) {
      if (statuses.includes(st)) return st;
    }
    return 'Granted';
  };

  const dropCategory = (l) => {
    const stops = l.stops || [];
    const dropStops = stops.map((s, i) => ({ s, i })).filter(x => x.s.drop_off);
    if (!dropStops.length) return null;
    const last = dropStops[dropStops.length - 1];
    if (last.s.type === 'pickup') return 'At the Shipper';
    if (last.i === stops.length - 1) return 'At the Delivery';
    return 'Waiting for Review';
  };

  let modeColumns = columns;
  let modeGrouped = groupedLoads;
  if (kanbanGroupBy === 'carrier') {
    modeColumns = carrierColumns.filter(id => CARRIER_KANBAN_COLUMNS.includes(id)).map(id => ({ id, title: id }));
    modeGrouped = modeColumns.reduce((acc, c) => { acc[c.id] = filteredLoads.filter(l => (l.carrier_status || 'Available') === c.id); return acc; }, {});
  } else if (kanbanGroupBy === 'xb') {
    // Cross-Border Stage board: active XB loads past pickup departure, grouped by derived stage; history shows Delivered/Released
    const cbLoads = filteredLoads.filter(l => l.cross_border && l.cb && (xbHistory ? !l.cb.active : l.cb.board_eligible));
    modeColumns = xbColumns.filter(id => XB_KANBAN_COLUMNS.includes(id)).map(id => ({ id, title: id }));
    modeGrouped = modeColumns.reduce((acc, c) => { acc[c.id] = cbLoads.filter(l => (l.cb.stage || l.cb_status) === c.id); return acc; }, {});
  } else if (kanbanGroupBy === 'drop') {
    const dropLoads = filteredLoads.filter(l => dropCategory(l));
    modeColumns = dropColumnsOrder.filter(id => DROP_KANBAN_COLUMNS.includes(id)).map(id => ({ id, title: id }));
    modeGrouped = modeColumns.reduce((acc, c) => { acc[c.id] = dropLoads.filter(l => dropCategory(l) === c.id); return acc; }, {});
  } else if (kanbanGroupBy === 'sched') {
    const stopItems = filteredLoads.flatMap(l => (l.stops || []).map((stop, stopIndex) => ({ id: `${l.id}::${stopIndex}`, load: l, stop, stopIndex })));
    modeColumns = schedColumnsOrder.filter(id => SCHED_KANBAN_COLUMNS.includes(id)).map(id => ({ id, title: id }));
    modeGrouped = modeColumns.reduce((acc, c) => { acc[c.id] = stopItems.filter(it => (it.stop.appointment_status || 'Needs Scheduling') === c.id); return acc; }, {});
  } else if (kanbanGroupBy === 'tracking') {
    const activeLoads = filteredLoads.filter(l => ACTIVE_LOAD_STATUSES.includes(l.status));
    modeColumns = trackingColumnsOrder.filter(id => TRACKING_KANBAN_COLUMNS.includes(id)).map(id => ({ id, title: id }));
    modeGrouped = modeColumns.reduce((acc, c) => { acc[c.id] = activeLoads.filter(l => trackingCategory(l) === c.id); return acc; }, {});
  }
  const kanbanColumnIds = kanbanGroupBy === 'load' ? activeColumns : modeColumns.map(c => c.id);

  const activeFilterLabel = availableFilterList.find(f => f.id === activeFilter)?.label || 'All Loads';
  const selectedCustomerName = selectedCustomerId ? customers.find(c => c.id === selectedCustomerId)?.name : null;
  const stuckCount = React.useMemo(() => loads.filter(isLoadStuck).length, [loads]);

  const isMobile = useIsMobile();
  useEffect(() => {
    if (!isMobile) return;
    document.querySelector(`[data-testid="kanban-groupby-${kanbanGroupBy}"]`)?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [isMobile, kanbanGroupBy, viewMode]);
  const mobileKanban = isMobile && viewMode === 'kanban' && !cbOnly && kanbanGroupBy !== 'checkcalls';

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-[var(--tms-text-secondary)]">Loading loads...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className={viewMode === 'list' || mobileKanban ? 'flex flex-col h-[calc(100vh-110px)]' : ''} data-testid="loads-page">
        {/* Mobile-first header */}
        <div className="mb-3 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Loads</h1>
              <p className="hidden md:block text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Manage shipments and tracking</p>
            </div>
            
            {/* Mobile: Stack controls vertically */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              
              {/* Action buttons row */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Filter chip bar lives BELOW the toolbar — see below the row */}

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-[#2A3441] rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`p-2 rounded ${viewMode === 'kanban' ? 'bg-[var(--tms-card)] shadow-sm' : ''}`}
                    data-testid="view-kanban"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-[var(--tms-card)] shadow-sm' : ''}`}
                    data-testid="view-list"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>

                {/* Column Manager (Kanban only) - Hidden for restricted users */}
                {viewMode === 'kanban' && !cbOnly && !isRestrictedUser && (
                  <div className="relative">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowColumnManager(!showColumnManager)}
                      data-testid="manage-columns-button"
                    >
                      <Settings className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Columns</span>
                    </Button>
                    {showColumnManager && (
                      <ColumnManager
                        activeColumns={activeColumns}
                        onToggleColumn={handleToggleColumn}
                        onClose={() => setShowColumnManager(false)}
                        carrierColumns={carrierColumns}
                        xbColumns={xbColumns}
                        dropColumns={dropColumnsOrder}
                        schedColumns={schedColumnsOrder}
                        trackingColumns={trackingColumnsOrder}
                        onToggleModeColumn={handleToggleModeColumn}
                      />
                    )}
                  </div>
                )}

                {/* CB Column Manager (Cross Border Kanban only) - Hidden for restricted users */}
                {viewMode === 'kanban' && cbOnly && !isRestrictedUser && (
                  <div className="relative">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowCBColumnManager(!showCBColumnManager)}
                      data-testid="manage-cb-columns-button"
                    >
                      <Settings className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Columns</span>
                    </Button>
                    {showCBColumnManager && (
                      <CBColumnManager
                        activeColumns={activeCBColumns}
                        onToggleColumn={handleToggleCBColumn}
                        activeCards={activeCBCards}
                        onToggleCard={handleToggleCBCard}
                        onClose={() => setShowCBColumnManager(false)}
                      />
                    )}
                  </div>
                )}

                {/* List Column Manager (List view only) */}
                {viewMode === 'list' && !isRestrictedUser && (
                  <div className="relative">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowListColumnManager(!showListColumnManager)}
                      data-testid="manage-list-columns-button"
                    >
                      <Settings className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Columns</span>
                    </Button>
                    {showListColumnManager && (
                      <ListColumnManager
                        activeColumns={activeListColumns}
                        onToggleColumn={handleToggleListColumn}
                        onClose={() => setShowListColumnManager(false)}
                        allColumns={ALL_LIST_COLUMNS}
                        isRestrictedUser={isRestrictedUser}
                      />
                    )}
                  </div>
                )}

                {/* Create Load button - Hidden for carriers and customers */}
                {!isRestrictedUser && (
                  <Button
                    data-testid="create-load-button"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={async () => {
                      try {
                        const res = await loadAPI.createBlank();
                        navigate(`/loads/${res.data.id}`);
                      } catch (err) {
                        toastApiError(toast, err, 'Load could not be created');
                      }
                    }}
                  >
                    <Plus className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Create Load</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Team / customer filters (shared with Dashboard) */}
        {!isRestrictedUser && directory && (
          <div className="mb-3">
            <TeamCustomerFilters filters={teamFilters} onChange={updateTeamFilters} directory={directory} compact testPrefix="loads-filter" />
          </div>
        )}

        {/* Temporary filter banner (Dashboard KPI / customer profile) */}
        {hasTempFilters && (
          <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900" data-testid="temp-filter-banner">
            <span className="text-sm">
              Showing <strong data-testid="temp-filter-label">{[
                activeFilter !== 'all' ? (availableFilterList.find(f => f.id === activeFilter)?.label || activeFilter) : null,
                tempCustomerId ? (customers.find(c => c.id === tempCustomerId)?.name || 'selected customer') : null,
              ].filter(Boolean).join(' · ')}</strong>
              <span className="ml-1 text-amber-700">({filteredLoads.length} load{filteredLoads.length !== 1 ? 's' : ''})</span>
            </span>
            <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => navigate('/loads')} data-testid="back-to-all-loads">
              Back to All Loads
            </Button>
          </div>
        )}

        {/* Filter chip bar */}
        <FilterChipBar
          statusFilters={isRestrictedUser ? CUSTOMER_CARRIER_FILTER_OPTIONS : FILTER_OPTIONS}
          activeFilter={activeFilter}
          onStatusChange={setActiveFilter}
          showStatusFilter={false}
          customers={[]}
          selectedCustomerId={null}
          onCustomerChange={() => {}}
          users={[]}
          selectedUsers={[]}
          onUserToggle={() => {}}
          onUsersClear={() => {}}
          showUserFilter={false}
          totalLoads={filteredLoads.length}
        />

        {/* Cross Border Kanban View - Special view for cross border loads */}
        {cbOnly && viewMode === 'kanban' ? (
          <CrossBorderKanbanView 
            loads={filteredLoads} 
            allLoads={loads}
            navigate={navigate} 
            onUpdateLoad={handleUpdateLoadCB}
            onDeleteLoad={handleDeleteLoad}
            activeCBColumns={activeCBColumns}
            onRemoveCBColumn={handleRemoveCBColumn}
            onReorderCBColumns={setActiveCBColumns}
            activeCBCards={activeCBCards}
          />
        ) : viewMode === 'kanban' ? (
          <>
            {/* Kanban group-by selector */}
            <div className="flex items-center gap-1 mb-3 bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-1 w-fit max-w-full overflow-x-auto shrink-0 [scrollbar-width:none]" data-testid="kanban-groupby-bar">
              {[
                { id: 'load', label: 'Load Status' },
                { id: 'carrier', label: 'Carrier Assignment' },
                { id: 'xb', label: 'Cross-Border Stage' },
                { id: 'drop', label: 'Drop Off Trailers' },
                { id: 'sched', label: 'Scheduling' },
                { id: 'tracking', label: 'Tracking' },
                { id: 'checkcalls', label: 'Check Calls' },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => changeKanbanGroupBy(m.id)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap shrink-0 ${kanbanGroupBy === m.id ? 'bg-[var(--tms-primary)] text-white' : 'text-[var(--tms-text-secondary)] hover:bg-[var(--tms-hover)]'}`}
                  data-testid={`kanban-groupby-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {kanbanGroupBy === 'xb' && (
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="xb-toolbar">
                <div className="flex items-center gap-1 bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-1">
                  <button onClick={() => setXbHistory(false)} className={`px-3 py-1 text-xs font-semibold rounded-md ${!xbHistory ? 'bg-[var(--tms-primary)] text-white' : 'text-[var(--tms-text-secondary)]'}`} data-testid="xb-view-active">Active</button>
                  <button onClick={() => setXbHistory(true)} className={`px-3 py-1 text-xs font-semibold rounded-md ${xbHistory ? 'bg-[var(--tms-primary)] text-white' : 'text-[var(--tms-text-secondary)]'}`} data-testid="xb-view-history">History / Completed</button>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setXbReviewOpen(true)} data-testid="xb-review-queue-btn">Review queue</Button>
                <span className="text-xs text-[var(--tms-text-muted)]">Stage is derived from recorded border milestones; cards cannot be dragged between stages.</span>
                <CbReviewQueueDialog open={xbReviewOpen} onOpenChange={setXbReviewOpen} />
              </div>
            )}
            {kanbanGroupBy === 'checkcalls' ? (
              <CheckCallsBoard />
            ) : mobileKanban ? (
              <MobileKanbanBoard
                columns={kanbanGroupBy === 'load' ? columns : modeColumns}
                grouped={kanbanGroupBy === 'load' ? groupedLoads : modeGrouped}
                facilities={facilities}
                mode={kanbanGroupBy}
                onToggleTracking={handleToggleTracking}
                unitLabel={kanbanGroupBy === 'sched' ? 'stops' : 'loads'}
              />
            ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={kanbanColumnIds} strategy={horizontalListSortingStrategy}>
                <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
                  {(kanbanGroupBy === 'load' ? columns : modeColumns).map((column) => (
                    <SortableColumn 
                      key={column.id} 
                      column={column} 
                      loads={(kanbanGroupBy === 'load' ? groupedLoads : modeGrouped)[column.id]} 
                      facilities={facilities}
                      onRemove={kanbanGroupBy === 'load' ? handleRemoveColumn : (id) => handleToggleModeColumn(kanbanGroupBy, id)}
                      onDeleteLoad={handleDeleteLoad}
                      showDropDays={kanbanGroupBy === 'drop'}
                      showCbDays={kanbanGroupBy === 'xb'}
                      onToggleTracking={kanbanGroupBy === 'tracking' ? handleToggleTracking : undefined}
                      renderCard={kanbanGroupBy === 'sched' ? (item) => <StopCard item={item} facilities={facilities} /> : kanbanGroupBy === 'xb' ? (l) => <LoadCard load={l} facilities={facilities} onDelete={handleDeleteLoad} showCb /> : undefined}
                      unitLabel={kanbanGroupBy === 'sched' ? 'stops' : 'loads'}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            )}
          </>
        ) : (
          <div className="stats-card flex-1 min-h-0 flex flex-col !p-0" data-testid="loads-list-card">
            <LoadsSpreadsheet
              loads={filteredLoads}
              facilities={facilities}
              customers={customers}
              isRestrictedUser={isRestrictedUser}
              listColumns={listColumns}
              fillHeight
              onLoadSaved={(updatedLoad) => setLoads(prev => prev.map(l => l.id === updatedLoad.id ? updatedLoad : l))}
              onLoadDuplicated={(newLoad) => setLoads(prev => [newLoad, ...prev])}
              emptyMessage={activeFilter !== 'all' ? 'No loads match the current filter' : 'No loads yet. Create your first load to get started.'}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Loads;
